// Mídias (biblioteca de imagens e vídeos) — ver docs/API.md do marketingbackgo, seção "Mídias".
// Carimbos (captured_at, created_at…) são "AAAA-MM-DDTHH:MM:SS" sem fuso; event_date é "AAAA-MM-DD".
// URLs (thumb_url, preview_url, upload_url) são SAS temporárias (~15 min) — nunca guardar.

export type MediaKind = 'image' | 'video';
export type MediaStatus = 'pending' | 'processing' | 'ready' | 'error';
export type MediaOrientation = 'landscape' | 'portrait' | 'square';
export type MediaSource = 'upload' | 'onedrive' | 'landing_page';
export type MediaAiType = 'produto' | 'ambiente' | 'evento' | 'pessoa' | 'arte' | 'outro';
export type MediaOrigin = 'ai' | 'user';
export type MediaRenditionKind = 'thumb' | 'preview' | 'whatsapp' | 'keyframe';
export type MediaUrlVariant = 'thumb' | 'preview' | 'original' | 'whatsapp';
export type MediaReprocessStep = 'probe' | 'renditions' | 'phash' | 'describe' | 'embed';
export type MediaSortBy =
  | 'relevance'
  | 'content_date'
  | 'created_at'
  | 'size_bytes'
  | 'file_name'
  | 'duration_seconds';

export interface MediaAssetTag {
  tag_id: number;
  category_id: number;
  category_slug: string;
  category_name: string;
  name: string;
  source: MediaOrigin;
  confidence?: number;
}

export interface MediaAssetProduct {
  sku: string;
  product_name?: string;
  source: MediaOrigin;
}

export interface MediaRendition {
  kind: MediaRenditionKind;
  width: number;
  height: number;
  size_bytes: number;
  content_type: string;
}

export interface MediaAsset {
  id: number;
  kind: MediaKind;
  title: string;
  original_file_name: string;
  content_type: string;
  extension: string;
  size_bytes: number;
  width?: number;
  height?: number;
  megapixels?: number;
  orientation?: MediaOrientation;
  duration_seconds?: number;
  frame_rate?: number;
  video_codec?: string;
  audio_codec?: string;
  bitrate_kbps?: number;
  color_space?: string;
  dpi_x?: number;
  dpi_y?: number;
  camera_make?: string;
  camera_model?: string;
  captured_at?: string;
  file_created_at?: string;
  file_modified_at?: string;
  content_date?: string;
  status: MediaStatus;
  status_message?: string;
  ai_description?: string;
  ai_type?: MediaAiType;
  ai_model?: string;
  ai_processed_at?: string;
  has_people: boolean;
  /** Texto do usuário (substitui o da IA na exibição quando preenchido). */
  description?: string;
  is_released_for_customers: boolean;
  is_preferred: boolean;
  event_id?: number;
  event_name?: string;
  location?: string;
  event_date?: string;
  source: MediaSource;
  source_path?: string;
  tags: MediaAssetTag[];
  products: MediaAssetProduct[];
  /** SAS de leitura (~15 min) — vem na listagem. */
  thumb_url?: string;
  /** Só no detalhe. */
  preview_url?: string;
  /** Só no detalhe. */
  renditions?: MediaRendition[];
  /** Relevância (com search). */
  score?: number;
  usage_count: number;
  created_at: string;
  created_by: string;
  updated_at?: string;
  last_updated_by?: string;
}

export interface MediaStats {
  total: number;
  images: number;
  videos: number;
  pending: number;
  processing: number;
  error: number;
  released: number;
  preferred: number;
  duplicates_pending: number;
  total_bytes: number;
}

export interface MediaFilter {
  search?: string;
  kind?: MediaKind | '';
  /** Todas as tags (AND). */
  tag_ids?: number[];
  event_id?: number;
  date_from?: string;
  date_to?: string;
  preferred?: boolean;
  released?: boolean;
  status?: MediaStatus | '';
  orientation?: MediaOrientation | '';
  source?: MediaSource | '';
  product_sku?: string;
  sort_by?: MediaSortBy;
  sort_direction?: 'asc' | 'desc';
  page?: number;
  page_size?: number;
}

export interface MediaSignedUrl {
  url: string;
  expires_at: string;
}

// ---------------------------------------------------------------- upload

export interface MediaUploadRequest {
  file_name: string;
  size_bytes: number;
  content_type: string;
  /** "AAAA-MM-DDTHH:MM:SS" do File.lastModified, no horário local. */
  file_last_modified?: string;
  /** Caminho dentro da pasta enviada (upload de pasta), ex.: "CLIENTES/Caixas de Couro/foto.jpg" — contexto para a IA. */
  relative_path?: string;
}

export interface MediaUploadTicket {
  media_id: number;
  upload_url: string;
  blob_path: string;
  expires_at: string;
}

// ---------------------------------------------------------------- edição

export interface MediaUpdateRequest {
  title: string;
  description?: string | null;
  event_id?: number | null;
  location?: string | null;
  event_date?: string | null;
}

export interface MediaFlagsRequest {
  is_released_for_customers?: boolean;
  is_preferred?: boolean;
}

/** Tag existente (`tag_id`) ou nova por nome dentro de uma categoria. */
export type MediaTagAssignment = { tag_id: number } | { category_id: number; name: string };

export interface MediaBulkRequest {
  media_ids: number[];
  add_tag_ids?: number[];
  remove_tag_ids?: number[];
  event_id?: number;
  clear_event?: boolean;
}

export interface MediaBulkFlagsRequest {
  media_ids: number[];
  is_released_for_customers?: boolean;
  is_preferred?: boolean;
}

export interface MediaBulkResult {
  updated: number;
}

export interface ProductSearchResult {
  sku: string;
  product_name: string;
}

// ---------------------------------------------------------------- duplicadas

export type DuplicateMatchKind = 'exact' | 'near' | 'similar';
export type DuplicateStatus = 'pending' | 'kept_both' | 'resolved';
export type DuplicateListStatus = 'pending' | 'resolved';
export type DuplicateAction = 'keep_a' | 'keep_b' | 'keep_both';

/** Resposta de POST /media/duplicates/:id/resolve. */
export interface ResolveDuplicateResult {
  /** Landing pages que passaram a usar a mídia que ficou. */
  landing_pages_updated: number;
  /** A troca nas páginas falhou (a mídia removida segue no ar até a limpeza). */
  warning?: string;
}

export interface DuplicateCandidate {
  id: number;
  match_kind: DuplicateMatchKind;
  /** Diferença do pHash em bits. */
  distance: number;
  /** Cosseno 0..1 da descrição da IA (pares `similar` da mesma sessão). */
  similarity?: number;
  status: DuplicateStatus;
  media_a: MediaAsset;
  media_b: MediaAsset;
  resolved_by?: string;
  resolved_at?: string;
  created_at: string;
}

// ---------------------------------------------------------------- taxonomia e eventos

export interface MediaTagCategory {
  id: number;
  slug: string;
  name: string;
  sort_order: number;
  is_active: boolean;
  tag_count: number;
}

export interface MediaTagCategoryFormData {
  name: string;
  slug?: string | null;
  sort_order?: number;
}

export interface MediaTag {
  id: number;
  category_id: number;
  category_slug: string;
  category_name: string;
  name: string;
  slug: string;
  synonyms: string[];
  usage_count: number;
  is_active: boolean;
}

export interface MediaTagCreateRequest {
  category_id: number;
  name: string;
  synonyms?: string[];
}

export interface MediaTagUpdateRequest {
  name: string;
  synonyms?: string[];
  is_active?: boolean;
}

export interface MediaEvent {
  id: number;
  name: string;
  location?: string;
  event_date?: string;
  description?: string;
  media_count: number;
  is_active: boolean;
}

export interface MediaEventFormData {
  name: string;
  location?: string | null;
  event_date?: string | null;
  description?: string | null;
}

// ---------------------------------------------------------------- importação OneDrive

export interface MediaImportSource {
  id: number;
  name: string;
  drive_owner_upn: string;
  folder_path: string;
  include_subfolders: boolean;
  is_active: boolean;
  last_run_at?: string;
  last_status?: string;
  last_message?: string;
  items_total: number;
  items_imported: number;
  items_skipped: number;
  items_error: number;
}

export interface MediaImportSourceFormData {
  name: string;
  drive_owner_upn: string;
  folder_path: string;
  include_subfolders: boolean;
  is_active: boolean;
}

export type MediaImportItemStatus = 'pending' | 'imported' | 'skipped' | 'error';

export interface MediaImportItem {
  id: number;
  source_id: number;
  drive_item_id: string;
  path: string;
  size_bytes: number;
  status: MediaImportItemStatus;
  media_id?: number;
  error?: string;
  updated_at: string;
}

// ---------------------------------------------------------------- rótulos

export const MEDIA_KIND_LABELS: Record<MediaKind, string> = { image: 'Imagem', video: 'Vídeo' };

export const MEDIA_STATUS_LABELS: Record<MediaStatus, string> = {
  pending: 'Na fila',
  processing: 'Processando',
  ready: 'Pronta',
  error: 'Erro',
};

export const MEDIA_ORIENTATION_LABELS: Record<MediaOrientation, string> = {
  landscape: 'Paisagem',
  portrait: 'Retrato',
  square: 'Quadrada',
};

export const MEDIA_SOURCE_LABELS: Record<MediaSource, string> = { upload: 'Upload', onedrive: 'OneDrive', landing_page: 'Landing pages' };

export const MEDIA_AI_TYPE_LABELS: Record<MediaAiType, string> = {
  produto: 'Produto',
  ambiente: 'Ambiente',
  evento: 'Evento',
  pessoa: 'Pessoa',
  arte: 'Arte',
  outro: 'Outro',
};

export const MEDIA_SORT_LABELS: Record<MediaSortBy, string> = {
  relevance: 'Relevância',
  content_date: 'Data do conteúdo',
  created_at: 'Data de envio',
  size_bytes: 'Tamanho',
  file_name: 'Nome do arquivo',
  duration_seconds: 'Duração',
};

export const IMPORT_ITEM_STATUS_LABELS: Record<MediaImportItemStatus, string> = {
  pending: 'Pendente',
  imported: 'Importado',
  skipped: 'Ignorado',
  error: 'Erro',
};

// ------------------------------------------------------------ compactar imagem (imageconverter)

export type MediaCompressionStatus = 'pending' | 'processing' | 'ready' | 'no_gain' | 'error' | 'accepted' | 'discarded';
export type MediaCompressionPresetKey = 'balanced' | 'strong' | 'custom';

export interface MediaCompressionPreset {
  key: Exclude<MediaCompressionPresetKey, 'custom'>;
  label: string;
  description: string;
  quality: number;
  max_side: number;
}

export interface MediaCompressionMeta {
  presets: MediaCompressionPreset[];
  min_quality: number;
  max_quality: number;
  min_side: number;
  min_gain_percent: number;
  /** false = conversor não configurado (Admin → Integrações). */
  available: boolean;
}

export interface MediaCompressionRequest {
  preset: MediaCompressionPresetKey;
  quality?: number;
  /** 0 = mantém o tamanho. */
  max_side?: number;
}

export interface MediaCompressionUsage {
  landing_pages: string[];
  email_campaigns: string[];
  has_public_copy: boolean;
}

export interface MediaCompression {
  id: number;
  media_id: number;
  preset: MediaCompressionPresetKey;
  quality: number;
  max_side: number;
  status: MediaCompressionStatus;
  original_size_bytes: number;
  original_width?: number;
  original_height?: number;
  original_content_type?: string;
  content_type?: string;
  extension?: string;
  size_bytes?: number;
  width?: number;
  height?: number;
  /** Redução em % (0 quando não reduziu). */
  gain_percent: number;
  error?: string;
  created_at: string;
  created_by: string;
  finished_at?: string;
  decided_at?: string;
  decided_by?: string;
  /** SAS de leitura — só em `ready`. */
  original_url?: string;
  candidate_url?: string;
  usage?: MediaCompressionUsage;
}

export interface MediaCompressionAcceptResult {
  media: MediaAsset;
  landing_pages: number;
  email_campaigns: number;
  public_url?: string;
  warnings: string[];
}
